import {useQuery, useQueryClient} from 'react-query'
import {getCredits} from 'api'
import {getItemFromStorage} from 'utils/storage'

/**
 * The signed-in member's Sharpennies balance (and recent history), as
 * one shared react-query entry.
 *
 * The header chip used to read `profile.credits` from local storage,
 * which is only rewritten at app load, login, and a couple of settings
 * saves — so it kept showing the old number after a reward claim, a
 * premium activation, or coins earned by posting. Everything that moves
 * the balance now calls useInvalidateCoinBalance and every chip reads
 * from here, so they cannot disagree.
 *
 * The stored profile's credits remain the fallback while the query is
 * loading, so the chip never flashes empty.
 */
export const COIN_BALANCE_QUERY_KEY = 'coinBalance'

export const useCoinBalanceQuery = (options = {}) => {
  const user = getItemFromStorage({name: 'user'})
  return useQuery(COIN_BALANCE_QUERY_KEY, getCredits, {
    enabled: !!user?.id,
    staleTime: 60 * 1000,
    ...options,
  })
}

const useCoinBalance = () => {
  const profile = getItemFromStorage({name: 'profile'})
  const {data} = useCoinBalanceQuery()
  return data?.balance ?? profile?.credits ?? 0
}

export const useInvalidateCoinBalance = () => {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries(COIN_BALANCE_QUERY_KEY)
}

export default useCoinBalance
